import { useMemo, useState } from 'react';
import type { DailySummary } from '../types';
import { formatKeystrokeCount } from '../lib/activityLog';
import {
  dailyProgressSeries,
  progressTrend,
  type DailyProgressPoint,
  type ProgressMetric,
} from '../lib/stats';

type RangeDays = 7 | 30 | 90;

interface ProgressChartProps {
  dailySummaries: Record<string, DailySummary> | undefined;
}

const METRIC_LABELS: Record<ProgressMetric, string> = {
  keystrokes: 'Tegn skrevet',
  minutes: 'Minutter',
  sessions: 'Øvelser',
  wpm: 'WPM',
  accuracy: 'Nøjagtighed',
};

export default function ProgressChart({ dailySummaries }: ProgressChartProps) {
  const [range, setRange] = useState<RangeDays>(30);
  const [metric, setMetric] = useState<ProgressMetric>('keystrokes');

  const points = useMemo(() => {
    const series = dailyProgressSeries(dailySummaries, range, metric);
    const first = series.findIndex(
      (p) => p.value !== null || p.sessionCount > 0,
    );
    return first > 0 ? series.slice(first) : series;
  }, [dailySummaries, range, metric]);
  const trend = useMemo(() => progressTrend(points), [points]);
  const hasData = points.some((p) => p.value !== null);
  const isVolumeMetric =
    metric === 'keystrokes' ||
    metric === 'minutes' ||
    metric === 'sessions';

  return (
    <section className="surface p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="label">Træning over tid</h2>
          <p className="mt-1 text-xs text-faint">
            {isVolumeMetric
              ? 'Alle øvelser tæller med — trin, varm-op, diktat, bevis og checkpoints.'
              : 'WPM og nøjagtighed fra trin-lektioner (ikke hastighedstests).'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <ToggleGroup
            value={String(range)}
            options={[
              ['7', '7 dage'],
              ['30', '30 dage'],
              ['90', '90 dage'],
            ]}
            onChange={(v) => setRange(Number(v) as RangeDays)}
          />
          <ToggleGroup
            value={metric}
            options={[
              ['keystrokes', 'Tegn'],
              ['minutes', 'Min.'],
              ['sessions', 'Øvelser'],
              ['wpm', 'WPM'],
              ['accuracy', 'Nøj.'],
            ]}
            onChange={(v) => setMetric(v as ProgressMetric)}
          />
        </div>
      </div>

      {!hasData ? (
        <p className="mt-4 text-sm text-muted">
          Ingen registreret aktivitet i denne periode endnu. Gennemfør en
          øvelse for at se din udvikling.
        </p>
      ) : (
        <>
          <div className="mt-4">
            <ProgressLineChart points={points} metric={metric} />
          </div>
          <TrendSummary trend={trend} metric={metric} range={range} />
        </>
      )}
    </section>
  );
}

function ToggleGroup({
  value,
  options,
  onChange,
}: {
  value: string;
  options: Array<[string, string]>;
  onChange: (v: string) => void;
}) {
  return (
    <div className="inline-flex rounded-lg bg-zinc-100 p-0.5 ring-1 ring-zinc-200 dark:bg-zinc-900/60 dark:ring-white/5">
      {options.map(([id, label]) => (
        <button
          key={id}
          type="button"
          onClick={() => onChange(id)}
          className={`rounded-md px-2.5 py-1 text-xs font-medium transition ${
            value === id
              ? 'bg-white text-strong shadow-sm ring-1 ring-zinc-200 dark:bg-zinc-800 dark:ring-white/10'
              : 'text-muted hover:text-strong'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function ProgressLineChart({
  points,
  metric,
}: {
  points: DailyProgressPoint[];
  metric: ProgressMetric;
}) {
  const width = 640;
  const height = 160;
  const pad = { top: 12, right: 12, bottom: 28, left: 44 };
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;

  const values = points
    .map((p) => p.value)
    .filter((v): v is number => v !== null);

  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);
  const padding =
    metric === 'wpm' ? 5 : metric === 'accuracy' ? 2 : maxVal > 0 ? maxVal * 0.1 : 1;
  const yMin = Math.max(0, Math.floor(minVal - padding));
  const yMax = Math.ceil(maxVal + padding) || 1;
  const ySpan = yMax - yMin || 1;

  const xForIndex = (i: number) =>
    pad.left +
    (points.length <= 1 ? innerW / 2 : (i / (points.length - 1)) * innerW);
  const yForValue = (v: number) =>
    pad.top + innerH - ((v - yMin) / ySpan) * innerH;

  const linePoints = points.flatMap((p, i) =>
    p.value === null
      ? []
      : [{ x: xForIndex(i), y: yForValue(p.value) }],
  );

  const yTicks = 4;
  const tickValues = Array.from({ length: yTicks + 1 }, (_, i) =>
    yMin + (ySpan * i) / yTicks,
  );

  const labelIndices = labelTickIndices(
    points.length,
    rangeLabelCount(points.length),
  );

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="h-auto w-full text-faint"
      role="img"
      aria-label={`Graf over daglig ${METRIC_LABELS[metric].toLowerCase()}`}
    >
      {tickValues.map((v) => {
        const y = yForValue(v);
        return (
          <g key={v}>
            <line
              x1={pad.left}
              y1={y}
              x2={width - pad.right}
              y2={y}
              stroke="currentColor"
              strokeOpacity={0.12}
            />
            <text
              x={pad.left - 6}
              y={y + 4}
              textAnchor="end"
              className="fill-current text-[10px]"
            >
              {formatAxisValue(metric, v)}
            </text>
          </g>
        );
      })}

      {linePoints.length > 1 && (
        <polyline
          fill="none"
          stroke="currentColor"
          className="text-indigo-500 dark:text-indigo-400"
          strokeWidth={2.5}
          strokeLinejoin="round"
          strokeLinecap="round"
          points={linePoints.map((pt) => `${pt.x},${pt.y}`).join(' ')}
        />
      )}

      {points.map((p, i) => {
        if (p.value === null) return null;
        const cx = xForIndex(i);
        const cy = yForValue(p.value);
        return (
          <g key={p.date}>
            <circle
              cx={cx}
              cy={cy}
              r={4}
              className="fill-indigo-500 dark:fill-indigo-400"
            />
            <title>
              {formatChartDate(p.date)}: {formatPointValue(metric, p.value)}
              {p.sessionCount > 1 ? ` · ${p.sessionCount} øvelser` : ''}
            </title>
          </g>
        );
      })}

      {labelIndices.map((i) => (
        <text
          key={points[i].date}
          x={xForIndex(i)}
          y={height - 6}
          textAnchor="middle"
          className="fill-current text-[10px]"
        >
          {formatChartDate(points[i].date, true)}
        </text>
      ))}
    </svg>
  );
}

function TrendSummary({
  trend,
  metric,
  range,
}: {
  trend: ReturnType<typeof progressTrend>;
  metric: ProgressMetric;
  range: RangeDays;
}) {
  const deltaLabel =
    trend.delta === null
      ? null
      : `${trend.delta >= 0 ? '+' : ''}${formatPointValue(metric, trend.delta)}`;

  const activeLabel =
    metric === 'wpm' || metric === 'accuracy'
      ? `${trend.curriculumDays} ${
          trend.curriculumDays === 1 ? 'dag' : 'dage'
        } med trin-lektioner`
      : `${trend.activeDays} ${
          trend.activeDays === 1 ? 'aktiv dag' : 'aktive dage'
        }`;

  return (
    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted">
      {deltaLabel &&
        (metric === 'wpm' || metric === 'accuracy'
          ? trend.curriculumDays >= 2
          : trend.activeDays >= 2) && (
          <span>
            <span
              className={
                (trend.delta ?? 0) >= 0
                  ? 'font-medium text-emerald-600 dark:text-emerald-400'
                  : 'font-medium text-amber-600 dark:text-amber-400'
              }
            >
              {deltaLabel}
            </span>{' '}
            over {range} dage
          </span>
        )}
      <span>{activeLabel}</span>
      {trend.totalKeystrokes > 0 && metric !== 'keystrokes' && (
        <span>{formatKeystrokeCount(trend.totalKeystrokes)} tegn i perioden</span>
      )}
      {trend.totalMinutes > 0 && metric !== 'minutes' && (
        <span>{trend.totalMinutes} min øvet</span>
      )}
      {trend.totalSessions > 0 && metric !== 'sessions' && (
        <span>
          {trend.totalSessions}{' '}
          {trend.totalSessions === 1 ? 'øvelse' : 'øvelser'}
        </span>
      )}
    </div>
  );
}

function formatAxisValue(metric: ProgressMetric, v: number): string {
  if (metric === 'wpm') return String(Math.round(v));
  if (metric === 'accuracy') return `${Math.round(v)}%`;
  if (metric === 'minutes') return String(Math.round(v));
  if (metric === 'sessions') return String(Math.round(v));
  if (v >= 1000) return formatKeystrokeCount(v);
  return String(Math.round(v));
}

function formatPointValue(metric: ProgressMetric, v: number): string {
  if (metric === 'wpm') return `${v.toFixed(0)} wpm`;
  if (metric === 'accuracy') return `${v.toFixed(0)}%`;
  if (metric === 'minutes') return `${v.toFixed(0)} min`;
  if (metric === 'sessions')
    return `${Math.round(v)} ${Math.round(v) === 1 ? 'øvelse' : 'øvelser'}`;
  return `${formatKeystrokeCount(v)} tegn`;
}

function labelTickIndices(length: number, count: number): number[] {
  if (length <= 1) return [0];
  const out: number[] = [];
  for (let i = 0; i < count; i++) {
    out.push(Math.round((i / (count - 1)) * (length - 1)));
  }
  return [...new Set(out)];
}

function rangeLabelCount(length: number): number {
  if (length <= 7) return Math.min(length, 7);
  if (length <= 30) return 5;
  return 6;
}

function formatChartDate(date: string, short = false): string {
  try {
    const d = new Date(date + 'T12:00:00');
    if (short) {
      return d.toLocaleDateString('da-DK', { day: 'numeric', month: 'short' });
    }
    return d.toLocaleDateString('da-DK', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  } catch {
    return date;
  }
}
